<!-- eslint-disable max-lines -->
<!-- eslint-disable @typescript-eslint/ban-ts-comment -->
<script setup lang="ts">
import { onBeforeUnmount, ref, nextTick, type Ref, watch, computed, onMounted } from 'vue'
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-sidebar-v2'
import '@/assets/css/leaflet/leaflet-sidebar.css' // ライブラリ[leaflet-sidebar-v2]のカスタムcssをインポート
import '@/assets/css/leaflet/leaflet-beautify-marker-icon.css' // BeautifyIconのスタイルをインポート
import { CustomTileLayer } from '@/libs/leaflet/customTimeLayer'
import { useNetworkStore } from '@/stores/networkStore'
import type { QueryBuildingRequestParam, QueryBuildingResult } from '@/types/accessor/api/building'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import type { Shape } from '@/types/accessor/api/common'
import type { ArgQueryAdminDistricts } from '@/types/accessor/api/adminDistricts/adminDistricts'
import { buildingColor } from '@/constants/ui/inspection/buildingInfo/buildingColor'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import { OVERALL_RESULT_VALUES } from '@/constants/accessor/api/emergencyInspectionResult/emergencyAssessment/overallResult'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import type { Building } from '@/types/accessor/api/emergencyInspectionResult/building/building'
import { checkLocationAndReverse } from '@/composables/inspection/leafletMapHelperFuncs'
import { getUserAttributesFromStore } from '@/modules/miiaFw/MiiaFwCognito'
import { MAX_ZOOM_LEVEL, Quadkey } from '@/modules/quadkeyGenerator/quadkeyGenerator'
import iconUrl from '@/assets/img/marker.svg'
import {
  useInspectionResultQueryStore,
  type QueryStateType
} from '@/stores/inspectionResultQueryStore'
import { BeautifyIcon } from '@/libs/leaflet/leaflet-beautify-marker-icon'
import CommonButton from '@/components/common/button/CommonButton.vue'
import { useCommonModalStore } from '@/stores/commonModalStore'

const props = defineProps({
  idName: { type: String, default: 'map' },
  showSideBar: { type: Boolean, default: false },
  disabledShowAll: { type: Boolean, default: false },
  disabledCurrentLocation: { type: Boolean, default: false }
})

const emit = defineEmits([
  'dataPush',
  'update:isBuilding',
  'clickedInspectionCandidate',
  'updateSelectedBuilding',
  'update:selectedAssessmentId'
])

const networkStore = useNetworkStore()
const inspectionResultStore = useInspectionResultStore()
const inspectionResultQueryStore = useInspectionResultQueryStore()
const commonModalStore = useCommonModalStore()

const sideMenuOpen = ref(false)
const map: Ref<L.Map | null> = ref(null)
const canFetchLocation = ref<boolean>(false)
const isBuilding: Ref<boolean> = ref(false)
const popupList: Ref<L.Marker | null> = ref(null)
const govCodeRef: Ref<string> = ref('')
const targetRef: Ref<{ location: number[]; shape?: Shape } | null> = ref(null)
const inspectionBuildings = ref<QueryBuildingResult[]>([])
const isNewBuilding = ref(false)
const currentLocationMarker: Ref<L.Marker | null> = ref(null)
const currentLocation = computed<L.LatLng | null>(() => {
  return currentLocationMarker.value?.getLatLng() || null
})
let geoWatchId: number | null = null

const defaultPoint: L.LatLngTuple = [35.68966674843021, 139.6918005385742]
const defaultZoomLevel = 18
const candidates = L.featureGroup()
const pinList = L.featureGroup()
const tile = L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png', {
  attribution: `
    <a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank' style="font-size:9px">地理院タイル</a>
    <span> | </span>
    <a href='https://nlftp.mlit.go.jp/cgi-bin/isj/dls/_choose_method.cgi' target='_blank' style="font-size:9px">国土交通省</a>
    <span> | </span>
    <a href='https://www.e-stat.go.jp/gis/statmap-search?page=1&type=2&aggregateUnitForBoundary=A&toukeiCode=00200521&toukeiYear=2015&serveyId=A002005212015&coordsys=1&format=gml' target='_blank' style="font-size:9px">e-Stat</a>
  `,
  maxZoom: 22,
  minZoom: 2,
  maxNativeZoom: 18
})
const iconImage = L.icon({
  iconUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
})
const flyToSpeed = {
  animate: true,
  duration: 0.5
}

/**
 * サイドバー表示切り替え
 */
const toggleSideBar = () => {
  sideMenuOpen.value = !sideMenuOpen.value
}

/**
 * サイドバーの設定
 * @param map
 */
const setUpSidebar = (map: L.Map) => {
  const sidebar = L.control
    .sidebar({
      autopan: false, // whether to maintain the centered map point when opening the sidebar
      closeButton: false, // whether t add a close button to the panes
      container: 'sidebar', // the DOM container or #ID of a predefined sidebar container that should be used
      position: 'right' // left or right
    })
    .addTo(map)
  // サイドバーの幅を参照するためにサイドバー要素を取得
  const sidebarElement = document.getElementById('sidebar')
  sidebar.on('opening', () => {
    if (sidebarElement) {
      const { offsetWidth } = sidebarElement
      map.panBy([-offsetWidth / 2, 0], { animate: true }) // 右側に出るので左にずらす
    }
  })
  sidebar.on('closing', () => {
    if (sidebarElement) {
      const { offsetWidth } = sidebarElement
      map.panBy([offsetWidth / 2, 0], { animate: true }) // 元に戻す
    }
  })
}

/**
 * マップの表示
 * propsでマップ生成時のDOMIdとなるidNameを取得する
 * @param latLng マップ生成時に表示する座標
 * @param zoomLevle ズームレベル
 * @param govCode 自治体コード
 * @returns なし
 */
const createMap = async (
  latLng: L.LatLngExpression = defaultPoint,
  zoomLevel: number = defaultZoomLevel,
  govCode?: string
) => {
  if (map.value !== null) {
    return
  }

  const newMap: L.Map = L.map(props.idName, { center: L.latLng(latLng), zoom: zoomLevel })
  newMap.addLayer(tile)
  L.control.scale({ position: 'bottomright', maxWidth: 200, imperial: false }).addTo(newMap)
  if (props.showSideBar) {
    setUpSidebar(newMap)
  }
  map.value = newMap

  const isOnline = networkStore.getIsOnline
  let customLayer = null
  if (!isOnline) {
    customLayer = new CustomTileLayer('')
  }

  if (map.value && customLayer) {
    customLayer.addTo(newMap)
  }
  map.value = newMap

  if (govCode != null && govCode !== '') {
    govCodeRef.value = govCode
  }

  // 現在地追跡を開始
  if (navigator.geolocation) {
    watchCurrentLocation()
  }

  // マップ調整
  map.value.invalidateSize()
}

/**
 * レイヤーの削除
 * @param なし
 * @returns なし
 */
const removeLayer = () => {
  if (map.value?.hasLayer(candidates)) {
    map.value.removeLayer(candidates)
  }
}

/**
 * マップの終了
 * @param なし
 * @returns なし
 */
const destroyMap = () => {
  // 現在地追跡を停止とマーカーの削除
  stopCurrentLocationWatch()
  if (map.value !== null) {
    map.value.removeLayer(tile)
    map.value.remove()
    map.value = null
  }
}

/**
 * 現在地を取得できるか判定する
 * @param なし
 * @returns true/false
 * @note iOS15以下はサポート外 https://support.apple.com/ja-jp/103267
 * @link https://developer.mozilla.org/ja/docs/Web/API/Permissions/query
 */
const getLocationPermission = async (): Promise<boolean> => {
  if (!navigator.geolocation) {
    return false
  }
  const { state } = await navigator.permissions.query({ name: 'geolocation' })
  return state !== 'denied'
}

/**
 * 現在地にズームインする
 * @param なし
 * @returns なし
 */
const showCurrentLocation = async () => {
  if (canFetchLocation.value) {
    const locationZoomLevel: number = 18
    flyToLocation(currentLocation.value, locationZoomLevel)
  } else {
    commonModalStore.openAlertModal({
      body: '現在地を取得できない端末です。',
      okTitle: '閉じる',
      okColor: 'secondary'
    })
  }
}

/**
 * 現在地と調査家屋を全体表示する
 * @param なし
 * @returns なし
 */
const showAllWithCurrentLocation = async () => {
  if (!(map.value instanceof L.Map)) return
  if (!canFetchLocation.value || currentLocation.value == null) {
    // 現在地が取得できない場合は既存の全体表示を行う
    showAllJudgedBuildingBound()
    return
  }

  // 一時的に現在地マーカーをcandidatesに追加してboundsを取得（調査建物と同じグループ）
  const tmpMarker = L.marker(currentLocation.value, { icon: iconImage })
  try {
    candidates.addLayer(tmpMarker)
    showAllJudgedBuildingBound()
  } finally {
    candidates.removeLayer(tmpMarker)
  }
}

/* 全ての調査済み結果を全体表示する
 * @param なし
 * @returns なし
 */
const showAllJudgedBuildingBound = async () => {
  if (!(map.value instanceof L.Map)) {
    return
  }
  // 判定済み建物のFeatureGroupのレイヤーが空なら処理しない
  if (candidates.getLayers().length === 0) {
    return
  }
  map.value.flyToBounds(candidates.getBounds(), flyToSpeed)
}

/**
 * 定期的に位置情報を取得して状態を更新する処理
 * @param なし
 * @returns なし
 */
const watchCurrentLocation = () => {
  // 既に実行中の場合は停止してから再開
  stopCurrentLocationWatch()
  if (!navigator.geolocation) return

  geoWatchId = navigator.geolocation.watchPosition(
    pos => {
      const currentLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude } as L.LatLng
      drawCurrentLocationCircle(currentLocation)
    },
    err => {
      console.debug('位置情報取得エラー', err)
    },
    { enableHighAccuracy: true }
  )
}

/**
 * 現在地の監視IDを破棄し、監視を停止する。現在地マーカーも地図から削除する
 * @param なし
 * @returns なし
 */
const stopCurrentLocationWatch = () => {
  if (geoWatchId !== null) {
    navigator.geolocation.clearWatch(geoWatchId)
    geoWatchId = null
  }

  // 現在地マーカーを削除
  if (currentLocationMarker.value && map.value?.hasLayer(currentLocationMarker.value)) {
    map.value.removeLayer(currentLocationMarker.value)
    currentLocationMarker.value = null
  }
}

/**
 * 現在位置にサークルを表示する
 * @param latlng 現在地の座標情報
 * @returns なし
 */
const drawCurrentLocationCircle = (latlng: L.LatLng) => {
  // マップが初期化されていない場合は処理しない
  if (map.value == null) return

  // 既存のマーカーがある場合は位置のみを更新
  if (currentLocationMarker.value && map.value.hasLayer(currentLocationMarker.value)) {
    currentLocationMarker.value.setLatLng(latlng)
    return
  }

  // BeautifyIconのオプション設定
  const options = {
    isAlphaNumericIcon: true,
    iconSize: [36, 36] as [number, number],
    iconAnchor: [18, 18] as [number, number],
    borderColor: 'blue',
    backgroundColor: 'blue',
    iconStyle: 'opacity: 0.3',
    iconUrl: ''
  }

  // 現在地マーカーを作成して地図に追加
  currentLocationMarker.value = L.marker(latlng, {
    icon: new BeautifyIcon(options) as L.Icon,
    draggable: false
  }).addTo(map.value)
}

/**
 * 建築物情報を取得し描画
 * @param latLng 経緯度
 * @param zoomLevel ズームレベル
 * @returns なし
 */
const drawInspectionBuildings = async (latLng: L.LatLng, zoomLevel: number) => {
  const buildingsApiQueryParams = {
    lat: latLng.lat,
    lng: latLng.lng,
    radius: zoomLevel > 18 ? 100 : 300,
    includingPersonalInformation: true
  } as QueryBuildingRequestParam

  candidates.clearLayers()
  inspectionBuildings.value = await getBuildingsData(buildingsApiQueryParams)
  inspectionBuildings.value.map(building => {
    drawInspectionBuilding(building)
  })
  drawAllJudgedBuildings()
}

/**
 * 対象建築物を描画（単一）
 * @param building 建物情報
 */
const drawInspectionBuilding = (building: QueryBuildingResult) => {
  const [isSkip, judgedItem] = isJudgedAndNotEditing(building.location)
  // 受け取った建物情報が、判定済かつ編集中ではない場合、処理をスキップ
  if (isSkip) return

  const shapeCoordinates = generateLatLngListBaseShape(building.shape.coordinates)
  const color = getSelectionColorByContext(judgedItem, 'candidate')
  const polygon = L.polygon(
    // @ts-ignore
    [shapeCoordinates],
    {
      color,
      fillOpacity: 0.5,
      building
    }
  )
  polygon.on('click', clickDrawInspectionBuilding)

  candidates.addLayer(polygon)
  candidates.addTo(map.value!)
}

/**
 * 対象建築物のクリックイベント処理
 * @param e イベント
 * @returns なし
 */
const clickDrawInspectionBuilding = (e: L.LeafletMouseEvent) => {
  const building = e.target.options.building as QueryBuildingResult
  // 建築物所在地をStoreに反映
  const buildingAddress = building.prefName + building.cityName + building.streetName
  setBuildingAddress(buildingAddress)

  const center = e.target.getBounds().getCenter()
  const location: [number, number] = [building.location[0], building.location[1]]
  const inspectionBuilding: Building = {
    buildingId: building.buildingId,
    location,
    quadkey: building.quadkey,
    shapeType: building.shape.type,
    shapeCoordinates: building.shape.coordinates
  }

  const shapeCoordinates = generateLatLngListBaseShape(building.shape.coordinates)
  commonHandleBuildingSelection(center, building.shape, shapeCoordinates, location)

  // 建築物情報をStoreに反映
  setBuilding(inspectionBuilding)
  // 行政区画API実行
  execAdminDistricts(e.latlng)
}

/**
 * 判定済建築物の描画
 */
const drawAllJudgedBuildings = () => {
  // 編集中家屋情報の取得
  const editAssessmentId = inspectionResultStore.commonResultSummary.assessmentId
  // 判定済建物描画
  const inspectionResultQueryList = inspectionResultQueryStore.getStateAll
  const judgedPolygons = []
  for (const item of inspectionResultQueryList.queryState) {
    if (!item.shapeCoordinates) continue
    if (editAssessmentId && item.assessmentId === editAssessmentId) continue

    const shape = generateLatLngListBaseShape(item.shapeCoordinates)
    const color = switchJudgedBuildingPolygonColor(item.overallResult ?? '')
    // ポリゴン作成
    // @ts-ignore
    const judgedPolygon = L.polygon([shape], {
      fillOpacity: 0.5,
      color,
      className: 'judged-building-polygon',
      bubblingMouseEvents: false
    })
    judgedPolygon.on('click', (e: L.LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(e)
    })
    judgedPolygons.push(judgedPolygon)
  }

  judgedPolygons.forEach(p => candidates.addLayer(p))
  candidates.addTo(map.value!)
}

/**
 * 建物選択時共通処理
 * @param center 中心緯度経度
 * @param shape  GeoJSONの形状の型
 * @param shapeCoordinates 形状
 * @param location 建物の緯度経度(JGD2011)
 * @returns なし
 */
const commonHandleBuildingSelection = (
  center: L.LatLng,
  shape: Shape,
  shapeCoordinates: number[][],
  location: [number, number]
) => {
  isBuilding.value = true
  isNewBuilding.value = false
  // 既存マーカーを削除
  if (popupList.value && map.value) {
    deleteTargetLayer()
  }

  // 判定済家屋かどうかをチェック
  const judgedItem = findJudgedItemByLocation(location)
  const color = getSelectionColorByContext(judgedItem, 'selected')
  pinList.addLayer(
    L.polygon(
      // @ts-ignore
      [shapeCoordinates],
      { weight: 4, pane: 'markerPane', color }
    )
  )
  pinList.addTo(map.value!)

  // 新しいマーカーを作成し、マップに追加
  popupList.value = L.marker(center, { icon: iconImage }).addTo(map.value!)

  targetRef.value = {
    ...targetRef.value,
    location,
    shape: shape
  }
}

/**
 * 選択した建築物の所在地をStoreに反映する
 * @param buildingAddress 建築物所在地
 * @returns なし
 */
const setBuildingAddress = (buildingAddress: string) => {
  inspectionResultStore.setBuildingOverview({
    ...inspectionResultStore.buildingOverview,
    buildingAddress
  })
}

/**
 * 選択した建築物情報をStoreに反映する
 * @param building 建築物情報
 * @returns なし
 */
const setBuilding = (building: Building) => {
  inspectionResultStore.setBuilding(building)
}

/**
 * 新規建築物情報をStoreに反映する
 * @param building 建築物情報
 * @returns なし
 */
const setNewBuilding = (building: Building) => {
  inspectionResultStore.setNewBuilding(building)
}

/**
 * Storeの建物情報を地図に反映する(クリック状態にする)
 * @param なし
 * @returns なし
 */
const setStoreToMap = () => {
  const building = inspectionResultStore.building
  if (building.shapeCoordinates && building.location && building.buildingId) {
    const shapeCoordinates = generateLatLngListBaseShape(building.shapeCoordinates)
    const bounds = L.latLngBounds(shapeCoordinates as L.LatLngTuple[])
    const center = bounds.getCenter()

    if (building.shapeType == null || building.shapeType === '') {
      throw new GlobalError([ERROR_CODES.BUILDING_OVERVIEW_TARGET_BUILDING_NOT_FOUND])
    }
    commonHandleBuildingSelection(
      center,
      { type: building.shapeType!, coordinates: building.shapeCoordinates },
      shapeCoordinates,
      building.location
    )

    const location = checkLocationAndReverse(building.location)
    flyToLocation(location, 20)
  }
}

/**
 * ムーブエンド時のイベントを有効にする
 * @param なし
 * @returns なし
 */
const controlMoveEndEvent = () => {
  map.value!.on('moveend', controlInspectionCandidates)
}

let moving = false
/**
 * ズームレベルに応じてレイヤーを表示する
 * @param なし
 * @returns なし
 */
const controlInspectionCandidates = async () => {
  if (moving) return
  moving = true
  const zoomLevel = map.value!.getZoom()
  if (zoomLevel ?? defaultZoomLevel > 16) {
    const center = map.value!.getCenter()
    await drawInspectionBuildings(center, zoomLevel)
  } else {
    removeLayer()
  }
  moving = false
}

/**
 * 新規建築物を追加しないを選択時と自治体外が選択された時に元々選択されていた建築物にピンを描画する。
 * @param なし
 * @returns なし
 */
const resetTargetBuilding = () => {
  if (targetRef.value?.location.length === 0 || targetRef.value == null) {
    deleteTargetLayer()
    return
  }

  if (targetRef.value?.shape?.coordinates && targetRef.value?.location) {
    // 新規建築物レイヤー削除
    deleteTargetLayer()

    // 直前に選択していたのが、新規建築物の場合
    if (isNewBuilding.value) {
      drawTargetBuilding(
        generateLatLngListBaseShape(targetRef.value.shape.coordinates),
        targetRef.value.location
      )
      flyToLocation([...targetRef.value.location].reverse() as L.LatLngExpression, 20)
      return
    }

    // 直前に選択していたのが、既存建築物の場合
    const bounds = L.latLngBounds(
      generateLatLngListBaseShape(targetRef.value.shape.coordinates) as L.LatLngTuple[]
    )
    const center = bounds.getCenter()
    popupList.value = L.marker(center, { icon: iconImage }).addTo(map.value!)

    // 判定済家屋かどうかをチェック
    const judgedItem = findJudgedItemByLocation(targetRef.value?.location)
    const color = getSelectionColorByContext(judgedItem, 'selected')
    const shape = generateLatLngListBaseShape(targetRef.value?.shape?.coordinates)
    pinList.addLayer(
      L.polygon(
        // @ts-ignore
        [shape],
        { weight: 4, pane: 'markerPane', color }
      )
    )
    pinList.addTo(map.value!)

    flyToLocation([...targetRef.value.location].reverse() as L.LatLngExpression, 20)
  }
}

/**
 * ポップアップとポリゴンを削除する
 * @param なし
 * @returns なし
 */
const deleteTargetLayer = () => {
  if (popupList.value) {
    map.value!.removeLayer(popupList.value)
  }
  pinList.eachLayer(pin => {
    map.value!.removeLayer(pin)
  })
}

/**
 * 調査対象家屋として選択された家屋に外形線とピンを表示する
 * @param coordinates 座標リスト
 * @param location 座標
 * @returns なし
 */
const drawTargetBuilding = (coordinates: number[][], location: number[]) => {
  if (map.value == null) {
    return
  }
  deleteTargetLayer()

  const [isSkip, judgedItem] = isJudgedAndNotEditing(location)
  // 受け取った建物情報が、判定済かつ編集中ではない場合、処理をスキップ
  if (isSkip) return

  const color = getSelectionColorByContext(judgedItem, 'selected')
  const polygon = L.polygon(
    // @ts-ignore
    coordinates,
    {
      color,
      weight: 4,
      fillOpacity: 0.5,
      pane: 'markerPane'
    }
  )

  pinList.addLayer(polygon)
  pinList.addTo(map.value!)
}

/**
 * マップをタッチすることで経緯度を取得できるようにする
 * @param なし
 * @returns なし
 */
const onTouchToGetLocation = () => {
  map.value!.on('click', getTouchLocation)
}

/**
 * マップ上でタップor左クリック時、経緯度を取得
 * @param e イベント
 * @returns なし
 */
const getTouchLocation = (e: { latlng: { lat: number; lng: number } }) => {
  if (isBuilding.value) {
    isBuilding.value = false
    return
  }
  const latlng: number[] = [e.latlng.lat, e.latlng.lng]
  emit('dataPush', latlng)
}

/**
 * 建築物情報のない場所をタッチされた際にデータを生成する
 * @param preLocation 選択された場所の緯度経度情報
 * @returns なし
 */
const clickedOnMap = async (preLocation: number[], shape: Shape) => {
  const latLng = {
    lat: preLocation[0],
    lng: preLocation[1]
  } as L.LatLng
  const searchParam = {
    ...latLng,
    govCode: govCodeRef.value,
    type: 'street'
  }

  const res = await accessDataBasedOnNetwork(
    'queryAdminDistrictsDaoApi',
    searchParam,
    networkStore.isOnline,
    () => {
      networkStore.setOfflineStatus()
    }
  ).catch(err => {
    // エラーの場合はエラー前のピンに戻す
    resetTargetBuilding()
    console.error(err)
    throw new GlobalError([ERROR_CODES.BUILDING_OVERVIEW_ADMIN_DISTRICTS_GET_FAILED])
  })

  if (res === undefined) return
  const adminDistrictsStreetItems = res.result
  const attribute = await getUserAttributesFromStore()
  if (
    adminDistrictsStreetItems.length === 0 ||
    adminDistrictsStreetItems[0]?.govCode === undefined ||
    adminDistrictsStreetItems[0]?.govCode !== attribute.attribute['disaster-commonattr'].govCode
  ) {
    resetTargetBuilding()
    throw new GlobalError([ERROR_CODES.BUILDING_OVERVIEW_NOT_APPLICABLE])
  }

  targetRef.value = {
    ...targetRef.value,
    location: [...preLocation].reverse(),
    shape: shape
  }
  isNewBuilding.value = true

  const quadkey = Quadkey.createFromLatLng(latLng, MAX_ZOOM_LEVEL).quadkey
  const inspectionBuilding: Building = {
    buildingId: undefined, // storeでuuidを生成する
    location: [preLocation[1], preLocation[0]], // 罹災では[経度,緯度］で取り扱うので注意。(leafletは[緯度,経度])
    quadkey: quadkey,
    shapeType: shape.type,
    shapeCoordinates: shape.coordinates
  }
  // 建築物情報をStoreに反映
  setNewBuilding(inspectionBuilding)

  nextTick(() => {
    drawTargetBuilding(
      generateLatLngListBaseShape(targetRef.value!.shape!.coordinates),
      targetRef.value!.location
    )
    flyToLocation([...targetRef.value!.location].reverse() as L.LatLngExpression, 20)
  })
}

/**
 * 判定済家屋を描画
 * @returns なし
 */
const drawJudgedBuildingsForHome = () => {
  const inspectionResultQueryList = inspectionResultQueryStore.getStateAll
  for (const item of inspectionResultQueryList.queryState) {
    if (!item.shapeCoordinates) continue
    const shape = generateLatLngListBaseShape(item.shapeCoordinates)
    drawJudgedBuilding(shape, item.assessmentId, item.overallResult ?? '', item.building)
  }
}

/**
 * 判定済家屋を描画（単一）
 * @param shapeCoordinates 対象建築物の形状一覧
 * @param assessmentId 判定 ID
 * @param overallResult 総合判定結果
 * @param building 建物情報
 * @returns なし
 */
const drawJudgedBuilding = (
  shapeCoordinates: number[][],
  assessmentId: string,
  overallResult: string,
  building: Building
) => {
  if (!overallResult && overallResult === '') return
  if (!map.value) return

  const color = switchJudgedBuildingPolygonColor(overallResult)
  candidates.addLayer(
    L.polygon(
      // @ts-ignore
      [shapeCoordinates],
      {
        color,
        fillOpacity: 0.5,
        assessmentId,
        building
      }
    ).on('click', clickJudgedBuildings)
  )
  candidates.addTo(map.value!)
}

/**
 * 判定済家屋のクリックイベント処理
 * @param e イベント
 * @returns なし
 */
const clickJudgedBuildings = (e: L.LeafletMouseEvent) => {
  isBuilding.value = true
  emit('update:isBuilding', isBuilding.value)
  // クリックされた家屋の調査情報を格納
  emit('update:selectedAssessmentId', e.target.options.assessmentId)

  // クリックされたポリゴンの中心を計算
  const center = e.target.getBounds().getCenter()

  // 既存マーカーを削除
  if (popupList.value && map.value) {
    deleteTargetLayer()
  }

  // 新しいマーカーを作成し、マップに追加
  popupList.value = L.marker(center, { icon: iconImage }).addTo(map.value!)

  // 選択建物をStoreに登録（編集時の初期選択用）
  const building = e.target.options.building as Building
  setBuilding(building)
}

/**
 * ポリゴン色切り替え
 * @param overallResult 総合判定結果
 * @return ポリゴン色
 */
const switchJudgedBuildingPolygonColor = (overallResult?: string): string => {
  switch (overallResult) {
    case OVERALL_RESULT_VALUES.RANK_A:
      return buildingColor.JUDGED_RANK_A
    case OVERALL_RESULT_VALUES.RANK_B:
      return buildingColor.JUDGED_RANK_B
    case OVERALL_RESULT_VALUES.RANK_C:
      return buildingColor.JUDGED_RANK_C
    default: {
      throw new Error(`想定外の値です: ${overallResult}`)
    }
  }
}

/**
 * 指定した経緯度まで飛行ズームで移動し、表示する
 * @param latLng 経緯度
 * @param zoomLevel ズームレベル
 * @returns なし
 */
const flyToLocation = (latLng: L.LatLngExpression | null, zoomLevel: number) => {
  if (map.value == null || latLng == null) {
    return
  }
  map.value.flyTo(latLng, zoomLevel, flyToSpeed)
}

/**
 * shapeからnumber[][]型のリストを作成する
 * 経緯度を逆にして返す
 * shape[0][0]はGeoJSONのMultiPorygon依存であること
 * shape[0][0]にメインの家屋形状が入っていること
 * @see {@link https://ja.wikipedia.org/wiki/GeoJSON}
 * @param shape 後方支援から来るデータ
 * @returns latLngs 経緯度リスト
 */
const generateLatLngListBaseShape = (shape: number[][][][]): number[][] => {
  const latLngs: number[][] = shape[0][0].map(side => {
    return [...side].reverse()
  })
  return latLngs
}

/**
 * 建築物情報取得
 * @param params 検索に使うパラメータ
 * @returns 建築物情報
 */
const getBuildingsData = async (
  params: QueryBuildingRequestParam
): Promise<QueryBuildingResult[]> => {
  const buildingsApiQueryParams = { ...params } as QueryBuildingRequestParam
  const inspectionBuildings = await accessDataBasedOnNetwork(
    'queryBuildingDaoApi',
    buildingsApiQueryParams,
    networkStore.isOnline,
    () => {
      networkStore.setOfflineStatus()
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ).catch((err: any) => {
    console.error(err.message)
    throw new GlobalError([ERROR_CODES.COMMON_BUILDING_GET_FAILED])
  })
  if (inspectionBuildings == undefined || inspectionBuildings.length <= 0) {
    return []
  }

  return inspectionBuildings
}

/**
 * 選択した建築物の緯度経度をもとに行政区画API実行
 * @param latLng 経緯度
 * @returns なし
 */
const execAdminDistricts = async (latLng: L.LatLng): Promise<void> => {
  const adminDistrictsQueryParams: ArgQueryAdminDistricts = {
    govCode: govCodeRef.value,
    type: 'street',
    lat: latLng.lat,
    lng: latLng.lng
  }

  await accessDataBasedOnNetwork(
    'queryAdminDistrictsDaoApi',
    adminDistrictsQueryParams,
    networkStore.getIsOnline,
    () => {
      networkStore.setOfflineStatus()
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ).catch((err: any) => {
    console.error(err.message)
    throw new GlobalError([ERROR_CODES.BUILDING_OVERVIEW_ADMIN_DISTRICTS_GET_FAILED])
  })
}

/**
 * 判定済家屋を建物IDで検索
 * @param location
 * @returns 判定済家屋情報
 */
const findJudgedItemByLocation = (location: number[]): QueryStateType | undefined => {
  const inspectionResultQueryList = inspectionResultQueryStore.getStateAll
  return inspectionResultQueryList.queryState.find(item => {
    return item.location && item.location[0] === location[0] && item.location[1] === location[1]
  })
}

/**
 * 判定済みかつ編集中ではない場合のスキップ条件
 * @param buildingId
 * @returns 判定済みかつ編集中ではないかフラグ, 判定済家屋情報
 */
const isJudgedAndNotEditing = (location: number[]): [boolean, QueryStateType | undefined] => {
  const judgedItem = findJudgedItemByLocation(location)
  const editAssessmentId = inspectionResultStore.commonResultSummary.assessmentId
  return [
    Boolean(judgedItem && editAssessmentId && judgedItem.assessmentId !== editAssessmentId),
    judgedItem
  ]
}

/**
 * 表示コンテキスト（候補/選択済み）に応じてポリゴンの色を決定
 * @param judgedItem
 * @param context 'candidate' は候補表示（未選択）、'selected' は選択表示
 * @returns 表示色（判定済み: EDIT／候補: SELECTABLE／選択: SELECTED）
 * @note
 * - 'candidate': 調査候補として表示する段階（未選択）。
 *   ユーザーがクリック可能な家屋の外形表示用の状態で、未判定なら灰色（SELECTABLE）を返す
 * - 'selected': ユーザーが選択済みの家屋。ピンと外形の強調表示用の状態で、未判定なら緑（SELECTED）を返す
 * - 場所が判定済み家屋に該当する場合は青（EDIT）を返す
 */
const getSelectionColorByContext = (
  judgedItem: QueryStateType | undefined,
  context: 'candidate' | 'selected'
): string => {
  if (judgedItem && judgedItem.overallResult) {
    return buildingColor.EDIT
  }
  return context === 'candidate' ? buildingColor.SELECTABLE : buildingColor.SELECTED
}

watch(targetRef, newValue => {
  if (newValue === null) return
  const isSelectedBuilding = newValue.location.length < 2 ? false : true
  emit('updateSelectedBuilding', isSelectedBuilding)
})

// ライフサイクル
onBeforeUnmount(() => {
  stopCurrentLocationWatch()
  destroyMap()
})

onMounted(async () => {
  canFetchLocation.value = await getLocationPermission()
})

defineExpose({
  createMap,
  generateLatLngListBaseShape,
  flyToLocation,
  controlMoveEndEvent,
  resetTargetBuilding,
  drawTargetBuilding,
  onTouchToGetLocation,
  clickedOnMap,
  drawJudgedBuildingsForHome,
  setStoreToMap
})
</script>

<template>
  <!-- TODO: leafletのCSSのカスタムクラスが親側のv-bindしたクラスによって
  消えるのでここで強制的にクラス指定 2022/01/07 rio_oshio -->
  <!-- <div class="map-wrapper"> -->
  <div
    class="map-container leaflet leaflet-container leaflet-touch leaflet-retina leaflet-fade-anim leaflet-grab leaflet-touch-drag leaflet-touch-zoom"
    ref="mymap"
    v-bind:id="idName"
  >
    <!-- TODO: サイドバーの表示について次期バージョンで見直し必要 -->
    <div v-if="showSideBar" id="sidebar" class="leaflet-sidebar collapsed">
      <!-- Nav tabs -->
      <div class="leaflet-sidebar-tabs">
        <ul role="tablist">
          <!-- top aligned tabs -->
          <li>
            <a href="#applicationList" role="tab" @click="toggleSideBar">
              <b-icon-arrow-right-short v-show="sideMenuOpen" width="20" height="20" />
              <b-icon-arrow-left-short v-show="!sideMenuOpen" width="20" height="20" />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div class="separate-block second-line">
    <div class="map-button" style="font-size: 1.5rem">
      <CommonButton
        v-if="!disabledShowAll"
        @click="showAllWithCurrentLocation"
        class="map-view-btn px-4 py-3 fw-bold"
        >全体表示</CommonButton
      >
      <CommonButton
        v-if="!disabledCurrentLocation"
        @click="showCurrentLocation"
        :class="canFetchLocation ? 'map-view-btn' : 'disable-map-view-btn'"
        class="ms-5 px-4 py-3 fw-bold"
      >
        現在地
      </CommonButton>
    </div>
  </div>
</template>

<style scoped>
:deep(.leaflet-interactive.judged-building-polygon) {
  cursor: not-allowed !important;
  pointer-events: auto;
}

:deep(.beautify-marker.circle) {
  cursor: inherit;
}

.map-container {
  height: 60vh;
}

.separate-block {
  display: flex;
  align-items: center;
  height: 60px;
}

.second-line {
  justify-content: flex-end;
}

.map-button {
  text-align: right;
  padding-right: 1.5rem;
}

.map-view-btn {
  background-color: #ffffff !important;
  color: gray;
}

.disable-map-view-btn {
  background-color: #e0e0e0 !important;
  color: gray !important;
}
</style>
